import { yupResolver } from "@hookform/resolvers/yup";
import { Button, FormControlLabel, Grid, Switch, TextField, Tooltip, makeStyles } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import * as yup from "yup";
import { actionShowSpinner } from "../../../../../../redux-flow/action/Wrapper/Spinner";
import { IBusinessUnitOption, getBuOptions } from "../../../../../../services/business.service";
import { createCommitteePackage, getCommitteePackageById, toggleActive, updateCommitteePackage } from "../../../../../../services/committePackages.service";
import { getGroupInfo } from "../../../../../../services/groups.service";
import AddButton from "../../../../../Common/AddButton";
import DialogCustom from "../../../../../Common/DialogCustom";
import ToastMessage from "../../../../../Common/ToastMessage";
import ModalBase from "../../../../../Pages/utils/ModalBase";
import ConfigureGroupModal, { IGroup } from "../../../Modals/CreateGroup";
import GroupsList from "../../../components/BoxLists/GroupsList";
import CommitteesFromPackageCard from "../../../components/Boxes/CommittesFromPackageCard";
import Header from '../../../components/Header';
import { AddNewRelationContainer, CardInfo, CommitteePackageInfoContainer, CommitteePackageLinks, Container, FormContainer, InputsContainer, SaveBuButtonContainer, SectionTitle, Title } from './styles';

const useStyles = makeStyles((theme) => ({
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
  },
}));

interface IPackageCommitteesData {
  name: string;
  business: string;
  businessId: string;
}

const schema = yup.object().shape({
  name: yup.string().required(),
  business: yup.string().required(),
  businessId: yup.string().required(),
});

const PackageCommittessConfiguration: React.FC = ({actionShowSpinner}: any) => {
  const location = useLocation<{id?: string}>();
  const { t } = useTranslation();
  const classes = useStyles();  
  const { register, handleSubmit, watch, setValue } = useForm<IPackageCommitteesData>({
    resolver: yupResolver(schema),
  });

  const effectHasRunned = useRef(false);

  const [toastMessage, setToastMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [configureGroupModalOpen, setConfigureGroupModalOpen] = useState(false);
  const [grouNameToCreate, setGroupNameToCreate] = useState('');
  const [adminPackage, setAdminPackage] = useState(false);
  const [committeePackageId, setCommitteePackageId] = useState<string | undefined>();
  const [businessOptions, setBusinessOptions] = useState<{id: string; name: string}[]>([]);
  const [groups, setGroups] = useState<{id: string; name: string; groupAdminLevel: number;}[]>([]);
  const [groupToUpdate, setGroupToUpdate] = useState<IGroup | undefined>(undefined);
  const [committees, setCommittees] = useState<{id: string; name: string}[]>([]);
  const [committeePackageActive, setCommitteePackageActive] = useState(false);
  const [disableCommitteePackageDialogOpened, setDisableCommitteePackageDialogOpened] = useState(false);

  useEffect(() => {    
    const params = new URLSearchParams(location.search);
    const id = params.get('id');

    if(id) {
      setCommitteePackageId(id);
    }
  }, [location]);

  useEffect(() => {
    if(!effectHasRunned.current) {
      fetchBusiness();
      
      effectHasRunned.current = true;
    }
      
    if(committeePackageId){
      fetchCommitteePackage(committeePackageId);
    }
  }, [committeePackageId, effectHasRunned.current]);

  const fetchCommitteePackage = useCallback(async (id: string) => {
    if(!id) return;
    
    try {  
      const { committees, groups, name, businessUnitId, businessUnitName, admin, active } = await getCommitteePackageById(id);

      setAdminPackage(admin);
      setValue('name', name);
      setValue('businessId', businessUnitId);
      setCommitteePackageActive(active);
      
      if(businessUnitName){
        setValue('business', businessUnitName);
      }

      setGroups(groups);
      setCommittees(committees);
    }catch(error: any) {
      const [ errorMessage ] = error?.response?.data?.errors || [t("softwareManagement.modules.business.errorFetchCommitteePackages")];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: 'error',
        autoHideDuration: 4000,
      });
    }
  }, [businessOptions]);

  const fetchBusiness = useCallback(async () => {
    actionShowSpinner(true);
    
    try {  
      const { businessUnits: allBusiness } = await getBuOptions();

      const formattedBusiness = allBusiness.map((business: IBusinessUnitOption) => {
        return {
          id: business.id,
          name: business.name,
        }
      });

      setBusinessOptions(formattedBusiness);
    }catch(error: any) {
      const [ errorMessage ] = error?.response?.data?.errors || [t("softwareManagement.modules.business.errorFetchBusiness")];

      setToastMessage({
        open: true,
        content: errorMessage || t("softwareManagement.modules.business.errorFetchBusiness"),
        type: 'error',
        autoHideDuration: 4000,
      });
    }
    
    actionShowSpinner(false);
  }, []);

  const onSubmit = useCallback(async (data: IPackageCommitteesData) => {
    if(!committeePackageId) {
      await createPackage(data);
    }else {
      await updatePackage(data);
    }
  }, [committeePackageId]);

  const createPackage = useCallback(async (data: IPackageCommitteesData) => {
    try {
      const createdCommittePackageId = await createCommitteePackage({
        businessUnitId: data.businessId,
        name: data.name,
      });

      setCommitteePackageId(createdCommittePackageId);

      setToastMessage({
        open: true,
        content: t("softwareManagement.modules.committeePackage.successCreation"),
        type: 'success',
        autoHideDuration: 4000,
      });
    }catch(err: any) {
      const [ errorMessage ] = err?.response?.data?.errors || [t("softwareManagement.modules.committeePackage.errorCreation")];

      setToastMessage({
        autoHideDuration: 4000,
        content: errorMessage || t("softwareManagement.modules.committeePackage.errorCreation"),
        open: true,
        type: 'error'
      });
    }
  }, []);

  const updatePackage = useCallback(async (data: IPackageCommitteesData) => {
    if(!committeePackageId) return;

    try {
      await updateCommitteePackage({
        businessUnitId: data.businessId,
        id: committeePackageId,
        name: data.name,
      });

      setToastMessage({
        open: true,
        content: t("softwareManagement.modules.committeePackage.successUpdate"),
        type: 'success',
        autoHideDuration: 4000,
      });
    }catch(err: any) {
      const [ errorMessage ] = err?.response?.data?.errors || [t("softwareManagement.modules.committeePackage.errorUpdate")];

      setToastMessage({
        autoHideDuration: 4000,
        content: errorMessage || t("softwareManagement.modules.committeePackage.errorUpdate"),
        open: true,
        type: 'error'
      })
    }
  }, [committeePackageId]);

  const handleCreateGroup = useCallback(async (id: string) => {
    setGroupNameToCreate('');

    actionShowSpinner(true);

    await fetchCommitteePackage(id);

    actionShowSpinner(false);
  }, []);

  const fetchGroup = useCallback(async (groupId: string) => {
    try {
      const group = await getGroupInfo(groupId);

      setGroupToUpdate({
        id: group.id,
        name: group.name,
        committeePackageName: group.committeePackageName,
        committeePackageId: group.committeePackageId,
        modalities: group.modalities,
        accessModel: {
          allResources: group.accessModelAllContent,
          onlyRelatedToDirectory: group.accessModelDirectoryBased,
          onlyRelatedToOwner: group.accessModelResponsibleBased,
          onlyRelatedToModalities: group.accessModelModalityBased,
        },
        participants: group.participants,
        selectedFunctionalities: group.functionalities.map((functionality: {id: string}) => functionality.id),
      });
    }catch(err: any) {
      const [ errorMessage ] = err?.response?.data?.errors || [t("softwareManagement.modules.business.errorFetchGroup")];

      setToastMessage({
        autoHideDuration: 4000,
        content: errorMessage || t("softwareManagement.modules.committeePackage.errorUpdate"),
        open: true,
        type: 'error'
      })
    }    
  }, []);	

  const handleUpdateGroup = useCallback(async (id: string) => {
    actionShowSpinner(true);

    await fetchGroup(id);
    setConfigureGroupModalOpen(true);

    actionShowSpinner(false);
  }, []);

  const handleClickOpen = (_event: any) => {
    setDisableCommitteePackageDialogOpened(true);
  };

  const handleToggleActive = useCallback(async() => {
    if(!committeePackageId) return;
    
    actionShowSpinner(true);
    
    try{
      await toggleActive(committeePackageId, !committeePackageActive);

      setCommitteePackageActive(!committeePackageActive);
    }catch(err: any) {
      const [ errorMessage ] = err?.response?.data?.errors || [t("softwareManagement.modules.business.errorToggleActive")];

      setToastMessage({
        autoHideDuration: 4000,
        content: errorMessage || t("softwareManagement.modules.business.errorToggleActive"),
        open: true,
        type: 'error'
      });
    }
    
    setDisableCommitteePackageDialogOpened(false);    
    actionShowSpinner(false);
  }, [committeePackageId, committeePackageActive]);
  
  return (
    <Container>
      <Header title= {t("softwareManagement.modules.committeePackage.settings")}></Header>

      <FormContainer>
        <CommitteePackageInfoContainer>
          <SectionTitle>
            <Title>
              {t("softwareManagement.modules.committeePackage.infomations")}
            </Title>
            
            {committeePackageId && (
              <FormControlLabel
                  control={
                  <Tooltip title={committeePackageActive ? t("deactivate") : t("activate")}>
                    <Switch
                      checked={committeePackageActive}
                      onChange={handleClickOpen}
                      color="primary"
                      />
                  </Tooltip>
                }
                label={committeePackageActive ? t("active") : t("inactive")}
              />
            )}
          </SectionTitle>

          <form onSubmit={handleSubmit(onSubmit)}>
            <InputsContainer container spacing={2}>
              <Grid item lg={6} xs={12}>
                <TextField
                  autoComplete='nope' 
                  id="name"
                  label="Nome do Pacote"
                  fullWidth
                  required={true}
                  error={!watch('name')} 
                  {...register("name")}
                  InputLabelProps={{ shrink: watch('name') ? true : false }} 
                />
              </Grid>

              <Grid item lg={6} xs={12}>
                <Autocomplete
                  options={businessOptions}
                  getOptionLabel={(option) => option.name || ""}
                  onChange={(event, newValue) => {
                    setValue('businessId', newValue?.id || '');
                  }}
                  value={watch('businessId') ? businessOptions.find((business) => business.id === watch('businessId')) : null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("softwareManagement.modules.business.title")}
                      required
                      error={!watch('business')}
                      value={watch('business')}
                      {...register("business")}
                    />
                  )}
                />
              </Grid>
            </InputsContainer>

            <SaveBuButtonContainer>
              <Button
                variant="contained"
                className={classes.button}
                type="submit"
              >
                Salvar
              </Button>
            </SaveBuButtonContainer>
          </form>
        </CommitteePackageInfoContainer>

        <CommitteePackageLinks>
          <CardInfo elevation={1}>
            <Title>
              {t("softwareManagement.modules.groups.titlePlural")}
            </Title>

            <AddNewRelationContainer>
              <TextField
                value={grouNameToCreate}
                autoComplete='nope' 
                name="groupName"
                id="groupName"
                label="Nome do Grupo"
                defaultValue=""
                fullWidth
                onChange={(e) => setGroupNameToCreate(e.target.value)}
              />

              <AddButton 
                disabled={!committeePackageId || !grouNameToCreate} 
                onClick={() => {
                  setGroupToUpdate(undefined);
                  setConfigureGroupModalOpen(true);
                }} 
              />
            </AddNewRelationContainer>

            <GroupsList
              groups={groups} 
              handleUpdateGroup={handleUpdateGroup}
            />
          </CardInfo>

          <CommitteesFromPackageCard 
            adminPackage={adminPackage}
            actionShowSpinner={actionShowSpinner} 
            committees={committees}
            onCommitteeListUpdated={(id: string) => fetchCommitteePackage(id)}
            committeePackageId={committeePackageId}
          />
        </CommitteePackageLinks>        
      </FormContainer>

      <ModalBase 
        open={configureGroupModalOpen} 
        setOpen={setConfigureGroupModalOpen}
        content={
          <ConfigureGroupModal
            setModalStatus={(value) => setConfigureGroupModalOpen(value)}
            groupToUpdate={groupToUpdate}
            packageIsAdmin={adminPackage}
            packageCommitteeId={committeePackageId || ''}
            adminPackage={adminPackage}
            groupName={grouNameToCreate}
            handleCreateGroup={handleCreateGroup}
            setToastMessage={setToastMessage}
          />
        }
      />      
      <DialogCustom
        open={disableCommitteePackageDialogOpened}
        handleClose={() => {
          setDisableCommitteePackageDialogOpened(false);
        }}
        handleChange={() => handleToggleActive()}
        title={t(`forms.dialog.alert`)}
        body={committeePackageActive ? t("softwareManagement.modules.committeePackage.textInactivate") : t("softwareManagement.modules.committeePackage.textActivate")}
        confirmText={t(`forms.dialog.yes`)}
        cancelText={t(`forms.dialog.no`)}
        alert={true}
      />
      <ToastMessage
        message={toastMessage.content}
        type={toastMessage.type || 'error'}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
    </Container>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  actionShowSpinner: (request: any) => dispatch(actionShowSpinner(request)),
});

const PackageCommittessConfigurationConnection = connect(
  null,
  mapDispatchToProps
)(PackageCommittessConfiguration);

export default PackageCommittessConfigurationConnection;